/**
 * Hand-off of a failed OAuth round-trip back to /auth/login.
 *
 * The login page must never render text taken from its own URL: a link like
 * `/auth/login?error=Your account is locked, call +1…` would print an
 * attacker's words on the real sign-in page. So the URL carries only a fixed
 * code, which the login page maps to a localized string, and an API-provided
 * explanation (already localized by the API) travels through sessionStorage,
 * which only this origin can write.
 */

/** The only `?error=` value /auth/login reacts to. */
export const OAUTH_FAILED_CODE = 'oauth_failed';

/** Email typed on (or handed back to) the login form, kept for this tab only. */
export const LOGIN_EMAIL_KEY = 'login_email';

const LOGIN_ERROR_MESSAGE_KEY = 'login_error_message';

/**
 * Apple posts its result cross-site to a server route, which has no access to
 * sessionStorage. That route leaves the API's explanation in this short-lived
 * cookie (scoped to the callback page) instead of the query string; the page
 * moves it into the sessionStorage hand-off above and deletes it.
 */
export const APPLE_AUTH_ERROR_COOKIE = 'apple_auth_error';
export const APPLE_CALLBACK_PATH = '/auth/apple/callback';

/**
 * Leave the callback page for /auth/login with the failure code, the optional
 * API message and email stashed for the form, and the deep-link redirect the
 * visitor originally came with (stashed as `oauth_redirect` before the
 * round-trip) so a retry still lands where they were heading.
 */
export function redirectToLoginAfterOAuthFailure({
  apiMessage,
  email,
}: {
  apiMessage?: string | null;
  email?: string | null;
} = {}): void {
  if (apiMessage) {
    sessionStorage.setItem(LOGIN_ERROR_MESSAGE_KEY, apiMessage);
  } else {
    sessionStorage.removeItem(LOGIN_ERROR_MESSAGE_KEY);
  }
  if (email) {
    sessionStorage.setItem(LOGIN_EMAIL_KEY, email);
  }

  const loginUrl = new URL('/auth/login', window.location.origin);
  loginUrl.searchParams.set('error', OAUTH_FAILED_CODE);
  const redirectPath = sessionStorage.getItem('oauth_redirect');
  if (redirectPath) {
    loginUrl.searchParams.set('redirect', redirectPath);
    sessionStorage.removeItem('oauth_redirect');
  }

  window.location.replace(loginUrl.toString());
}

/** Read and forget the API message stashed by {@link redirectToLoginAfterOAuthFailure}. */
export function consumeOAuthFailureMessage(): string | null {
  const message = sessionStorage.getItem(LOGIN_ERROR_MESSAGE_KEY);
  sessionStorage.removeItem(LOGIN_ERROR_MESSAGE_KEY);
  return message;
}

/** Read and delete the Apple route's error cookie (see {@link APPLE_AUTH_ERROR_COOKIE}). */
export function consumeAppleAuthErrorCookie(): string | null {
  const prefix = `${APPLE_AUTH_ERROR_COOKIE}=`;
  const raw = document.cookie
    .split('; ')
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
  document.cookie = `${APPLE_AUTH_ERROR_COOKIE}=; Max-Age=0; Path=${APPLE_CALLBACK_PATH}`;
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}
